import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { courseService, learningPathService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import EmptyState from './EmptyState';

const FeedbackDetailsModal = ({ 
  open, 
  onClose, 
  feedbackId, 
  type = 'course' // 'course' or 'learningpath'
}) => {
  const [feedbackDetails, setFeedbackDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (open && feedbackId) {
      fetchFeedbackDetails();
    }
  }, [open, feedbackId, type]);

  const fetchFeedbackDetails = async () => {
    setLoading(true);
    try {
      let response;
      if (type === 'learningpath') {
        response = await learningPathService.getL1FeedbackDetails(feedbackId);
      } else {
        response = await courseService.getL1FeedbackDetails(feedbackId);
      }
      setFeedbackDetails(response.data.data);
    } catch (error) {
      console.error('Error fetching feedback details:', error);
      showToast('Failed to load feedback details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFeedbackDetails(null);
    onClose();
  };

  const getTitle = () => {
    return type === 'learningpath' ? 'Learning Path Feedback Details' : 'Course Feedback Details';
  };

  const getItemLabel = () => {
    return type === 'learningpath' ? 'Learning Path' : 'Course';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          {getTitle()}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography>Loading feedback details...</Typography>
          </Box>
        ) : feedbackDetails ? (
          <Box>
            {/* Questions and Answers */}
            <Typography variant="h6" gutterBottom>
              Feedback Responses
            </Typography>
            
            {feedbackDetails.questions && feedbackDetails.questions.length > 0 ? (
              feedbackDetails.questions.map((item, index) => (
                <Box key={item.id} sx={{ mb: 3 }}>
                  <Typography 
                    variant="body1" 
                    fontWeight={400} 
                    color="text.primary"
                    gutterBottom
                  >
                    Q{index + 1}. {item.question}
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 0, 
                      //backgroundColor: 'grey.50',
                      border: 'none',
                      //borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Answer: {item.answer || 'No answer provided'}
                    </Typography>
                  </Paper>
                  {index < feedbackDetails.questions.length - 1 && (
                    <Divider sx={{ mt: 1 }} />
                  )}
                </Box>
              ))
            ) : (
              // The shared empty state, with its own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review):
              // its own centred line.
              <EmptyState framed={false} title="No feedback questions found" message={null} />
            )}
          </Box>
        ) : (
          // The shared empty state, with its own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): its
          // own centred line.
          <EmptyState framed={false} title="No feedback details available" message={null} />
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDetailsModal;